import CaptchaKeyType from '../types/CaptchaKeyType';
import type HighlightArticleData from '@/bps/types/HighlightArticleData';

declare global {
  interface Window {
    __APP: any;
    __globalConfig: GlobalConfig;
  }
}

interface GlobalConfig {
  language: string,
  country: string,
  currency: string,
  csrfToken: CsrfToken,
  activeVoucherCode: string,
  apiUrls: { [key: string]: any },
  onRequestLightBoxUrl: string,
  cookieLightBoxUrl: string,
  assetPath: string,
  fallbackImage: string,
  fbAppId?: string,
  inputAnonymization: string,
  sessionTimeout: { [key: string]: string },
  trustedShopUrl?: string,
  trustedShopId?: string,
  klarnaLibSrc?: string,
  klarnaClientId?: string,
  wishlistUrl: string,
  basketUrl: string,
  checkoutUrl: string,
  isWishlistPopupEnabled: boolean,
  addSuccessorToBasket: boolean,
  isNativeApp: boolean,
  salutationPersonDefault?: string,
  abVariant: {[key: string]: unknown},
  showSoldoutAlternatives?: boolean,
  nativeAppConfig?: {
    isNativeApp: boolean,
    mobileEngageContactId?: string,
    emailHash: string,
  },
  partnerUrl?: string,
  cspNonce?: string,
  toplevelCategoryId: string,
  categoryId?: string,
  itemId?: string,
  turnstileConfig?: Record<CaptchaKeyType, {key: string}>,
  highlightArticleMap: Record<string, HighlightArticleData>,
}

interface CsrfToken {
  name: string,
  value: string,
}

let config: JsGlobalConfig;

class JsGlobalConfig {
  get apiUrls() {
    return window.__globalConfig.apiUrls;
  }

  getCsrfTokenData() {
    let data = {};
    data[window.__globalConfig.csrfToken.name] = window.__globalConfig.csrfToken.value;
    return data;
  }

  getCsrfTokenHeader() {
    return {
      'X-CSRF-TOKEN': window.__globalConfig.csrfToken.value,
    }
  }

  getLanguage() {
    return window.__globalConfig.language || '';
  }

  getCountry() {
    return window.__globalConfig.country || '';
  }

  getCurrency() {
    return window.__globalConfig.currency || '';
  }

  getAssetPath() {
    return window.__globalConfig.assetPath || '';
  }

  getFallbackImage() {
    return window.__globalConfig.fallbackImage || '';
  }

  getInputAnonymization() {
    return window.__globalConfig.inputAnonymization || '';
  }

  getSessionTimeoutData() {
    return window.__globalConfig.sessionTimeout || {}
  }

  getWishlistUrl() {
    return window.__globalConfig.wishlistUrl;
  }

  getBasketUrl() {
    return window.__globalConfig.basketUrl;
  }

  getCheckoutUrl() {
    return window.__globalConfig.checkoutUrl;
  }

  isWishlistPopupEnabled() {
    return window.__globalConfig.isWishlistPopupEnabled;
  }

  addSuccessorToBasket() {
    return window.__globalConfig.addSuccessorToBasket;
  }

  getSalutationPersonDefault() {
    return window.__globalConfig.salutationPersonDefault;
  }

  getAbVariants() {
    return window.__globalConfig.abVariant;
  }

  getShowSoldoutAlternatives() {
    return window.__globalConfig.showSoldoutAlternatives;
  }

  getPartnerUrl() {
    return window.__globalConfig?.partnerUrl;
  }

  getCspNonce() {
    return window.__globalConfig?.cspNonce;
  }

  getToplevelCategoryId() {
    return window.__globalConfig?.toplevelCategoryId;
  }

  getCaptchaConfig(type: CaptchaKeyType) {
    let turnstileConfig = window.__globalConfig.turnstileConfig[type] ?? null;

    if (turnstileConfig) {
      return {
        key: turnstileConfig.key,
      };
    }

    return null;
  }

  getShopName() {
    const assetPath = window.__globalConfig.assetPath || '';
    const pathParts = assetPath.split('/').filter(Boolean);
    return pathParts[pathParts.length - 1] || '';
  }

  getIsNativeApp() {
    return window.__globalConfig.isNativeApp;
  }

  getNativeAppConfig() {
    return window.__globalConfig.nativeAppConfig;
  }

  getHighlightArticleData(highlightId: string): HighlightArticleData | null {
    const highlightArticleMap = window.__globalConfig.highlightArticleMap;
    return highlightArticleMap[highlightId] || null;
  }

  getCategoryId() {
    return window.__globalConfig.categoryId;
  }

  getItemId() {
    return window.__globalConfig.itemId;
  }
}

function jsGlobalConfig(): JsGlobalConfig {
  if (config) {
    return config;
  } else {
    config = new JsGlobalConfig();
    return config;
  }
}

export default jsGlobalConfig;
